import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  Car,
  CheckCircle2,
  Clock,
  ExternalLink,
  Handshake,
  Lock,
  MapPin,
  MessageCircle,
  Navigation,
  Search,
  ShieldCheck,
  Sparkles,
  Ticket,
  Utensils,
} from "lucide-react";

const joshyUrl = "https://www.getjoshy.com";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
};

const capabilityGroups = [
  {
    icon: <Utensils className="w-5 h-5" />,
    title: "Restaurant reservations",
    description:
      "Joshy starts with Vancouver restaurant reservations, helping you move from where to eat to a table that works.",
    prompts: ["Book Italian for two tonight.", "Find a patio near Yaletown."],
  },
  {
    icon: <Car className="w-5 h-5" />,
    title: "Nearby parking",
    description:
      "Plan the full outing with parking options near the restaurant or destination you choose.",
    prompts: ["Where should I park near the restaurant?", "Find parking close to Gastown dinner."],
  },
  {
    icon: <Search className="w-5 h-5" />,
    title: "Dining guidance",
    description:
      "Compare cuisines, neighborhoods, timing, and practical details without opening a pile of tabs.",
    prompts: ["Best sushi for a quiet date?", "What works for a family dinner?"],
  },
  {
    icon: <Ticket className="w-5 h-5" />,
    title: "Free events",
    description:
      "Find local events that fit the plan before or after the reservation.",
    prompts: ["Any free events tonight?", "What can we do after dinner?"],
  },
  {
    icon: <Navigation className="w-5 h-5" />,
    title: "Services and recreation",
    description:
      "Discover nearby services, recreation options, and useful local stops around the same conversation.",
    prompts: ["Find something active nearby.", "Show me local services close by."],
  },
  {
    icon: <CalendarDays className="w-5 h-5" />,
    title: "Full local plans",
    description:
      "Joshy helps connect food, parking, timing, and things to do into one practical Vancouver plan.",
    prompts: ["Plan Saturday evening.", "Build a simple downtown itinerary."],
  },
];

const productPillars = [
  {
    title: "Vancouver-first",
    description:
      "Joshy is focused on local context, starting with restaurants, neighborhoods, parking, events, services, and recreation in Vancouver.",
  },
  {
    title: "Booking before browsing",
    description:
      "The product is designed around getting to a useful next step, not just giving you a generic list of links.",
  },
  {
    title: "One chat for the outing",
    description:
      "Ask naturally, compare options, adjust the plan, and keep the details in one place.",
  },
  {
    title: "Built by Aaron DPenha",
    description:
      "Joshy is part of The App Fam portfolio alongside ScribeMind and Selah Daily.",
  },
];

const steps = [
  {
    number: "01",
    title: "Ask Joshy",
    description: "Tell Joshy what you want to book or discover in Vancouver.",
    example: "\"Find a great dinner spot for four near Kitsilano this Friday.\"",
  },
  {
    number: "02",
    title: "Compare the plan",
    description: "Joshy narrows the options and adds practical details like parking, timing, and nearby ideas.",
    example: "Restaurants, availability, location fit, parking, and what to do nearby.",
  },
  {
    number: "03",
    title: "Move toward booking",
    description: "Choose the option that fits and continue through the Joshy flow.",
    example: "Less searching, more getting the evening sorted.",
  },
];

const scenarios = [
  {
    title: "Date night",
    text: "\"Joshy, book a cozy restaurant and find parking nearby.\"",
  },
  {
    title: "Family dinner",
    text: "\"Find a family-friendly spot near downtown with an early table.\"",
  },
  {
    title: "Weekend plan",
    text: "\"Plan Saturday with dinner, a free event, and something fun after.\"",
  },
  {
    title: "Visitor help",
    text: "\"I am new to Vancouver. Where should I eat near the seawall?\"",
  },
];

const differentiators = [
  "Joshy ties Aaron DPenha and The App Fam to a clear Vancouver AI booking use case.",
  "Joshy is specific enough for search: restaurant reservations, parking, events, services, and recreation.",
  "Joshy gives www.theappfam.com an indexable product page instead of sending every search signal off-site.",
  "Joshy is presented alongside ScribeMind and Selah Daily as part of one creator portfolio.",
];

const trustPoints = [
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: "Clear product focus",
    description: "Restaurant reservations first, with related local help around the same plan.",
  },
  {
    icon: <Lock className="w-5 h-5" />,
    title: "Policies available",
    description: "Joshy privacy and terms are linked separately for users who need the details.",
  },
  {
    icon: <Clock className="w-5 h-5" />,
    title: "Built to save time",
    description: "The goal is less search friction and a faster path from idea to outing.",
  },
];

const JoshPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="josh-page">
      <section className="min-h-[calc(100vh-5rem)] flex items-center py-16 md:py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center"
          >
            <div className="w-full max-w-[342px] sm:max-w-4xl">
              <p className="caption mb-4">Joshy by Aaron DPenha</p>
              <h1 className="heading-1 mb-6">
                AI restaurant reservations and{" "}
                <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                  local help in Vancouver.
                </span>
              </h1>
              <p className="body-lg max-w-3xl mb-6">
                Joshy is an AI booking assistant for Vancouver restaurant reservations first.
              </p>
              <p className="body-text max-w-2xl mb-10">
                Built by Aaron DPenha and The App Fam, Joshy helps with nearby parking,
                dining guidance, free events, services, and recreation discovery in the
                same chat so local plans feel easier to finish.
              </p>
              <div className="w-full max-w-[342px] sm:max-w-none flex flex-col sm:flex-row gap-4">
                <a
                  href={joshyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="josh-hero-cta"
                >
                  Open Joshy
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center"
                  data-testid="josh-hero-secondary-cta"
                >
                  Contact Aaron
                </Link>
              </div>
            </div>

            <motion.div
              {...fadeInUp}
              className="relative w-full max-w-[342px] sm:max-w-none rounded-[2.5rem] bg-[#F5F5F7] border border-zinc-100 p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(217,70,239,0.14),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(147,51,234,0.1),transparent_30%)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm">
                  <Sparkles className="w-4 h-4 text-fuchsia-500" />
                  Vancouver plans, one chat
                </div>
                <div className="mt-8 space-y-4">
                  {[
                    "Book a table for two in Mount Pleasant tonight.",
                    "Find nearby parking and something free to do after dinner.",
                    "Compare sushi spots that are quiet enough for a date.",
                    "Plan a Saturday evening downtown with dinner and recreation.",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl bg-white p-4 text-sm md:text-base text-zinc-700 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-zinc-500">
                  Restaurant reservations, parking, events, services, and recreation.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Capability Map</p>
            <h2 className="heading-2 mb-6">What Joshy helps with.</h2>
            <p className="body-text">
              Joshy connects the important pieces of a local plan so users can ask,
              compare, and move toward booking without scattering the work across tabs.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {capabilityGroups.map((item) => (
              <motion.div key={item.title} variants={fadeInUp} className="feature-card">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 mb-5">
                  {item.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{item.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600 mb-5">{item.description}</p>
                <div className="space-y-2">
                  {item.prompts.map((prompt) => (
                    <div key={prompt} className="rounded-2xl bg-[#F5F5F7] px-4 py-3 text-sm text-zinc-600">
                      {prompt}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">Why Joshy</p>
              <h2 className="heading-2 mb-6">A product people can search for and understand.</h2>
              <p className="body-text mb-8">
                Joshy now has a clear home inside The App Fam: an Aaron DPenha product
                for Vancouver restaurant reservations and local discovery.
              </p>
              <div className="space-y-4">
                {differentiators.map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                    <p className="body-text">{point}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Core Product Principles</p>
              <h3 className="heading-3 mb-6">How Joshy should feel.</h3>
              <div className="space-y-5">
                {productPillars.map((pillar) => (
                  <div key={pillar.title}>
                    <p className="font-medium text-zinc-900">{pillar.title}</p>
                    <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">How It Works</p>
            <h2 className="heading-2 mb-6">Simple on the surface. Useful underneath.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {steps.map((step) => (
              <motion.div key={step.number} variants={fadeInUp} className="feature-card">
                <p className="text-sm font-semibold tracking-[0.2em] text-zinc-400 mb-5">{step.number}</p>
                <h3 className="heading-3 mb-4">{step.title}</h3>
                <p className="body-text mb-5">{step.description}</p>
                <div className="rounded-2xl bg-[#F5F5F7] px-5 py-4 text-sm text-zinc-600">
                  {step.example}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Daily Scenarios</p>
            <h2 className="heading-2 mb-6">What using Joshy can feel like.</h2>
            <p className="body-text">
              A clear local assistant for dinner plans, parking, events, recreation,
              and useful Vancouver discovery.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
          >
            {scenarios.map((scenario) => (
              <motion.div key={scenario.title} variants={fadeInUp} className="feature-card">
                <p className="caption mb-4">{scenario.title}</p>
                <p className="text-lg font-medium tracking-tight text-zinc-900 leading-relaxed">
                  {scenario.text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">The Creator Connection</p>
              <h2 className="heading-2 mb-6">Joshy belongs in the Aaron DPenha search footprint.</h2>
              <p className="body-text mb-8">
                The App Fam now gives search engines a direct relationship between
                Aaron DPenha, Joshy, ScribeMind, and Selah Daily. That relationship is
                visible in page copy, internal links, metadata, and structured data.
              </p>
              <div className="rounded-[1.5rem] bg-white p-6 border border-zinc-100">
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-400 mb-3">
                  The idea in one line
                </p>
                <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-900">
                  Joshy helps Vancouver plans move from search to booking.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">The App Fam Portfolio</p>
              <h3 className="heading-3 mb-4">One creator, three clear products.</h3>
              <div className="space-y-4 text-zinc-600">
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">
                    Joshy: AI restaurant reservations and Vancouver local discovery.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">
                    ScribeMind: AI study notes, summaries, and lecture-based exams.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Handshake className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">
                    Selah Daily: sermon transcripts, summaries, devotionals, and prayer rhythms.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Privacy and Control</p>
            <h2 className="heading-2 mb-6">A useful assistant should be understandable.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {trustPoints.map((point) => (
              <motion.div key={point.title} variants={fadeInUp} className="feature-card text-center">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-5 text-zinc-600">
                  {point.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{point.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{point.description}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div {...fadeInUp} className="mt-12 feature-card max-w-3xl mx-auto text-center">
            <p className="caption mb-4">Legal</p>
            <h3 className="heading-3 mb-4">Read the full Joshy policies separately.</h3>
            <p className="body-text mb-8">
              Privacy details and platform terms are available on dedicated Joshy pages.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/privacy/joshy"
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="josh-privacy-page-link"
              >
                Joshy Privacy Policy
              </Link>
              <Link
                to="/terms/joshy"
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="josh-terms-page-link"
              >
                Joshy Terms
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-zinc-900">
        <div className="container-main text-center">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <p className="caption text-zinc-500 mb-4">Try Joshy</p>
            <h2 className="heading-2 text-white mb-6">Open Joshy and start with a Vancouver plan.</h2>
            <p className="text-lg text-zinc-400 leading-relaxed mb-10">
              Joshy lives at getjoshy.com, and this page helps people discover how it
              connects to Aaron DPenha and The App Fam.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={joshyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
                data-testid="josh-open-final-cta"
              >
                Open Joshy
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/apps"
                className="h-12 px-8 rounded-full border border-zinc-700 text-white font-medium text-sm hover:bg-zinc-800 transition-all inline-flex items-center justify-center"
                data-testid="josh-apps-cta"
              >
                Explore The App Fam
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JoshPage;
